import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Box, Button, Typography } from '@mui/material';
import React, { Component } from 'react';
import { connect, MapStateToProps } from 'react-redux';

import { deleteExercise, resetExerciseSearch, searchExercises } from '../../actions/exercises';
import { ExerciseSearchFilters, ExerciseSearchItem } from '../../api/exercises.interface';
import { withRouter, WithRouterProps } from '../../common/hooks/withParams';
import { ReduxState } from '../../reducers';
import ExerciseSearch from './ExerciseSearch';

interface ComponentProps {}

interface ReduxProps {
    deletingTag?: number;
    hasLoadedSearch: boolean;
    items: ExerciseSearchItem[];
    loadingList: boolean;
    searchFilters: ExerciseSearchFilters;
}

interface ReduxActionProps {
    deleteExercise: (tag: number, onSuccess?: () => void) => void;
    resetExerciseSearch: () => void;
    searchExercises: (filters: ExerciseSearchFilters) => void;
}

type Props = ComponentProps & ReduxProps & ReduxActionProps & WithRouterProps;

class ExerciseManagementPage extends Component<Props> {
    onCreateExerciseClick = () => {
        this.props.navigate('/exercises/new');
    };

    onDeleteExercise = (item: ExerciseSearchItem) => {
        this.props.deleteExercise(item.tag);
    };

    onEditExercise = (index: number) => {
        this.props.navigate(`/exercises/${index}/edit`);
    };

    onResetSearch = () => {
        this.props.resetExerciseSearch();
    };

    onSearchExercises = (filters: ExerciseSearchFilters) => {
        this.props.searchExercises(filters);
    };

    render() {
        const { deletingTag, hasLoadedSearch, items, loadingList, searchFilters } = this.props;

        return (
            <Box maxWidth={1200}>
                <Box sx={{ alignItems: { md: 'flex-start', xs: 'stretch' }, display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 1.5, justifyContent: 'space-between', mb: 2.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: { md: '1.6rem', xs: '1.4rem' }, fontWeight: 700, mb: 0.25 }} variant="h4">Exercises</Typography>
                        <Typography color="text.secondary" sx={{ fontSize: '0.875rem', maxWidth: 720 }} variant="body1">
                            Manage your workout library. Search, create, edit, and remove exercises from one operational workspace.
                        </Typography>
                    </Box>
                    <Box sx={{ position: { md: 'sticky', xs: 'static' }, top: 24 }}>
                        <Button onClick={this.onCreateExerciseClick} size="small" startIcon={<AddRoundedIcon />} sx={{ fontSize: '0.82rem', px: 1.5, py: 0.7 }} variant="contained">
                            Add New Exercise
                        </Button>
                    </Box>
                </Box>
                <ExerciseSearch
                    currentFilters={searchFilters}
                    deletingTag={deletingTag}
                    hasLoadedSearch={hasLoadedSearch}
                    items={items}
                    loadingList={loadingList}
                    onDeleteExercise={this.onDeleteExercise}
                    onEditExercise={this.onEditExercise}
                    onReset={this.onResetSearch}
                    onSearch={this.onSearchExercises}
                    selectionMode="none"
                />
            </Box>
        );
    }
}

const mapStateToProps: MapStateToProps<ReduxProps, ComponentProps, ReduxState> = ({ exercises }: ReduxState) => ({
    deletingTag: exercises.deletingTag,
    hasLoadedSearch: exercises.hasLoadedSearch,
    items: exercises.items,
    loadingList: exercises.loadingList,
    searchFilters: exercises.searchFilters,
});

export default connect<ReduxProps, ReduxActionProps, ComponentProps, ReduxState>(mapStateToProps, {
    deleteExercise,
    resetExerciseSearch,
    searchExercises,
})(withRouter(ExerciseManagementPage));